import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Row, 
    Col, 
    Typography, 
    Statistic, 
    Progress, 
    List, 
    Tag, 
    Button, 
    Select, 
    DatePicker,
    Space,
    Divider,
    Tooltip,
    Empty
} from 'antd';
import { 
    RiseOutlined, 
    ShoppingCartOutlined, 
    StarOutlined, 
    HeartOutlined,
    CalendarOutlined,
    DollarOutlined,
    TrophyOutlined,
    FireOutlined,
    EyeOutlined,
    MessageOutlined
} from '@ant-design/icons';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const UserAnalytics = () => {
    const { user } = useAuth();
    const [analytics, setAnalytics] = useState({});
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState([
        dayjs().subtract(30, 'days'),
        dayjs()
    ]);
    const [timeframe, setTimeframe] = useState('30d');

    useEffect(() => {
        fetchAnalytics();
    }, [dateRange, timeframe]);

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            const response = await api.getUserAnalytics({
                start_date: dateRange[0].format('YYYY-MM-DD'),
                end_date: dateRange[1].format('YYYY-MM-DD'),
                timeframe
            });
            
            if (response.success) {
                setAnalytics(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    const getChartData = (type, data) => {
        const baseConfig = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'bottom',
                },
            },
        };

        switch (type) {
            case 'spending':
                return {
                    ...baseConfig,
                    data: {
                        labels: data.labels,
                        datasets: [{
                            label: 'Spending (₹)',
                            data: data.values,
                            borderColor: 'rgb(75, 192, 192)',
                            backgroundColor: 'rgba(75, 192, 192, 0.2)',
                            tension: 0.1
                        }]
                    }
                };
            
            case 'bookings':
                return {
                    ...baseConfig,
                    data: {
                        labels: data.labels,
                        datasets: [{
                            label: 'Bookings',
                            data: data.values,
                            backgroundColor: [
                                'rgba(255, 99, 132, 0.8)',
                                'rgba(54, 162, 235, 0.8)',
                                'rgba(255, 205, 86, 0.8)',
                                'rgba(75, 192, 192, 0.8)',
                                'rgba(153, 102, 255, 0.8)',
                            ]
                        }]
                    }
                };
            
            case 'preferences':
                return {
                    ...baseConfig,
                    data: {
                        labels: data.labels,
                        datasets: [{
                            data: data.values,
                            backgroundColor: [
                                '#FF6384',
                                '#36A2EB',
                                '#FFCE56',
                                '#4BC0C0',
                                '#9966FF',
                                '#FF9F40'
                            ]
                        }]
                    }
                };
            
            default:
                return baseConfig;
        }
    };

    const renderInsights = () => {
        const insights = analytics.insights || [];
        
        if (insights.length === 0) {
            return <Empty description="No insights available" />;
        }

        return (
            <List
                dataSource={insights}
                renderItem={(insight) => (
                    <List.Item>
                        <Card size="small" className="w-full">
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0">
                                    {insight.type === 'spending' && <DollarOutlined className="text-green-500 text-lg" />}
                                    {insight.type === 'preference' && <HeartOutlined className="text-red-500 text-lg" />}
                                    {insight.type === 'trend' && <RiseOutlined className="text-blue-500 text-lg" />}
                                    {insight.type === 'achievement' && <TrophyOutlined className="text-yellow-500 text-lg" />}
                                </div>
                                <div className="flex-1">
                                    <Text strong>{insight.title}</Text>
                                    <div className="mt-1">
                                        <Text type="secondary">{insight.description}</Text>
                                    </div>
                                    {insight.action && (
                                        <Button type="link" size="small" className="p-0 mt-1">
                                            {insight.action}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Card>
                    </List.Item>
                )}
            />
        );
    };

    return (
        <div className="px-4 sm:px-6 py-6">
            <div className="max-w-7xl mx-auto">
                <div className="mb-6">
                    <Title level={2}>
                        <TrophyOutlined className="mr-2" />
                        Your Analytics
                    </Title>
                    <Text type="secondary">
                        Track your activity, spending, and preferences
                    </Text>
                </div>

                {/* Controls */}
                <Card className="mb-6" size="small">
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} sm={8} md={6}>
                            <Text strong>Time Period:</Text>
                            <Select
                                value={timeframe}
                                onChange={setTimeframe}
                                style={{ width: '100%' }}
                            >
                                <Option value="7d">Last 7 days</Option>
                                <Option value="30d">Last 30 days</Option>
                                <Option value="90d">Last 90 days</Option>
                                <Option value="1y">Last year</Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={8} md={6}>
                            <Text strong>Custom Range:</Text>
                            <RangePicker
                                value={dateRange}
                                onChange={setDateRange}
                                style={{ width: '100%' }}
                            />
                        </Col>
                        <Col xs={24} sm={8} md={6}>
                            <Button type="primary" onClick={fetchAnalytics}>
                                Refresh Data
                            </Button>
                        </Col>
                    </Row>
                </Card>

                {/* Key Metrics */}
                <Row gutter={[16, 16]} className="mb-6">
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic
                                title="Total Spent"
                                value={analytics.total_spent || 0}
                                prefix={<DollarOutlined />}
                                precision={2}
                                valueStyle={{ color: '#3f8600' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic
                                title="Total Bookings"
                                value={analytics.total_bookings || 0}
                                prefix={<ShoppingCartOutlined />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic
                                title="Average Rating"
                                value={analytics.average_rating || 0}
                                prefix={<StarOutlined />}
                                precision={1}
                                valueStyle={{ color: '#faad14' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic
                                title="Favorites"
                                value={analytics.total_favorites || 0}
                                prefix={<HeartOutlined />}
                                valueStyle={{ color: '#f5222d' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Charts */}
                <Row gutter={[16, 16]} className="mb-6">
                    <Col xs={24} lg={12}>
                        <Card title="Spending Trend" size="small">
                            <div style={{ height: '300px' }}>
                                {analytics.spending_chart && (
                                    <Line {...getChartData('spending', analytics.spending_chart)} />
                                )}
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                        <Card title="Booking Types" size="small">
                            <div style={{ height: '300px' }}>
                                {analytics.bookings_chart && (
                                    <Doughnut {...getChartData('bookings', analytics.bookings_chart)} />
                                )}
                            </div>
                        </Card>
                    </Col>
                </Row>

                <Row gutter={[16, 16]} className="mb-6">
                    <Col xs={24} lg={12}>
                        <Card title="Cuisine Preferences" size="small">
                            <div style={{ height: '300px' }}>
                                {analytics.preferences_chart && (
                                    <Pie {...getChartData('preferences', analytics.preferences_chart)} />
                                )}
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                        <Card title="Monthly Activity" size="small">
                            <div style={{ height: '300px' }}>
                                {analytics.activity_chart && (
                                    <Bar {...getChartData('activity', analytics.activity_chart)} />
                                )}
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Top Lists */}
                <Row gutter={[16, 16]} className="mb-6">
                    <Col xs={24} md={8}>
                        <Card title="Top Vendors" size="small">
                            <List
                                dataSource={analytics.top_vendors || []}
                                renderItem={(vendor, index) => (
                                    <List.Item>
                                        <div className="flex items-center justify-between w-full">
                                            <div className="flex items-center gap-2">
                                                <Text strong>#{index + 1}</Text>
                                                <Text>{vendor.name}</Text>
                                            </div>
                                            <Tag color="blue">{vendor.count} orders</Tag>
                                        </div>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card title="Favorite Cuisines" size="small">
                            <List
                                dataSource={analytics.favorite_cuisines || []}
                                renderItem={(cuisine, index) => (
                                    <List.Item>
                                        <div className="flex items-center justify-between w-full">
                                            <Text>{cuisine.name}</Text>
                                            <Progress 
                                                percent={cuisine.percentage} 
                                                size="small" 
                                                showInfo={false}
                                            />
                                        </div>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card title="Recent Activity" size="small">
                            <List
                                dataSource={analytics.recent_activity || []}
                                renderItem={(activity) => (
                                    <List.Item>
                                        <div className="flex items-center gap-2">
                                            <CalendarOutlined />
                                            <Text className="text-sm">{activity.description}</Text>
                                            <Text type="secondary" className="text-xs">
                                                {dayjs(activity.created_at).fromNow()}
                                            </Text>
                                        </div>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Insights */}
                <Card title="Personalized Insights" size="small">
                    {renderInsights()}
                </Card>
            </div>
        </div>
    );
};

export default UserAnalytics;
